import { message } from 'antd';
import { TwoFactorSetup } from 'api-calls/two-factor';
import { Button } from 'common/ui/button';
import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import TwoFactorCodeInput from './TwoFactorCodeInput';

// Adding the account to an authenticator app: a QR code of the otpauth URL,
// the secret for manual entry, and a code to confirm it works.
export default function TwoFactorSetupForm({
  setup,
  onSubmit,
  isPending,
  error,
  submitLabel = 'Xác nhận',
  onCancel,
}: {
  setup: TwoFactorSetup;
  onSubmit: (code: string) => void;
  isPending?: boolean;
  error?: string;
  submitLabel?: string;
  onCancel?: () => void;
}) {
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [code, setCode] = useState('');

  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(setup.otpauth_url, { width: 200, margin: 1 })
      .then(url => !cancelled && setQrDataUrl(url))
      .catch(() => !cancelled && setQrDataUrl(''));
    return () => {
      cancelled = true;
    };
  }, [setup.otpauth_url]);

  const copySecret = () => {
    navigator.clipboard.writeText(setup.secret);
    message.success('Đã sao chép khóa');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) return;
    onSubmit(code);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <ol className="list-decimal space-y-1 pl-5 text-sm text-gray-600">
        <li>
          Mở ứng dụng xác thực (Google Authenticator, Microsoft Authenticator,
          1Password...).
        </li>
        <li>Quét mã QR bên dưới hoặc nhập khóa thủ công.</li>
        <li>Nhập mã 6 số hiển thị trong ứng dụng.</li>
      </ol>

      <div className="flex justify-center">
        {qrDataUrl ? (
          <img
            src={qrDataUrl}
            alt="Mã QR xác thực hai lớp"
            width={200}
            height={200}
            className="rounded border"
          />
        ) : (
          <div className="h-[200px] w-[200px] rounded border bg-gray-50" />
        )}
      </div>

      <div>
        <div className="mb-1 text-sm text-gray-500">Khóa nhập thủ công</div>
        <div className="flex items-center gap-2">
          <code className="flex-1 break-all rounded bg-gray-100 px-2 py-1 font-mono text-sm">
            {setup.secret}
          </code>
          <Button type="button" variant="outline" size="sm" onClick={copySecret}>
            Sao chép
          </Button>
        </div>
      </div>

      <div>
        <label className="block mb-1 font-medium">Mã xác thực</label>
        <TwoFactorCodeInput value={code} onChange={setCode} disabled={isPending} />
      </div>
      {error && <div className="text-red-500 text-sm">{error}</div>}

      <Button
        type="submit"
        className="w-full"
        disabled={isPending || code.length !== 6}
      >
        {isPending ? 'Đang xác nhận...' : submitLabel}
      </Button>
      {onCancel && (
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={onCancel}
          disabled={isPending}
        >
          Hủy
        </Button>
      )}
    </form>
  );
}
